import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalJourneyTaskAttachment } from "./types";

// DELETE /api/portal/journeys/attachments/:id — partner removes an
// attachment from a task. Optimistic remove from the attachments cache;
// rollback on error.

type Input = { id: string; progressId: string };
type Context = {
  previous: PortalJourneyTaskAttachment[] | undefined;
  key: ReturnType<typeof queryKeys.portal.journeys.taskAttachments>;
};

export function useRemoveTaskAttachment() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiClientError, Input, Context>({
    mutationFn: async ({ id }) => {
      await apiFetch<void>(`/portal/journeys/attachments/${id}`, {
        method: "DELETE",
      });
    },
    onMutate: async ({ id, progressId }) => {
      const key = queryKeys.portal.journeys.taskAttachments(progressId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<PortalJourneyTaskAttachment[]>(key);
      if (previous) {
        queryClient.setQueryData<PortalJourneyTaskAttachment[]>(
          key,
          previous.filter((a) => a.id !== id),
        );
      }
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to remove attachment");
    },
    onSettled: (_data, _err, { progressId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.taskAttachments(progressId),
      });
    },
  });
}
